<template>
  <!-- Teleport: RouteEditorPanel.vue (родитель) использует,
       который по спеке CSS делает элемент containing block для потомков
       position:fixed (как transform/filter/perspective/will-change) - без
       Teleport этот оверлей позиционировался бы относительно узкой 300px
       панели вместо всего экрана. -->
  <Teleport to="body">
  <div class="fixed inset-0 z-[105] flex items-center justify-center bg-ink/50 px-4 py-6">
    <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-md flex flex-col paper-card rounded-md overflow-hidden">
      <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
        <p :id="dialogTitleId" class="flex-1 text-[15px] font-semibold truncate">
          {{ isExisting ? t('map.routeEditor.saveDialog.titleExisting') : t('map.routeEditor.saveDialog.titleNew') }}
        </p>
        <button
          type="button"
          @click="$emit('cancel')"
          :aria-label="t('map.routeEditor.saveDialog.cancel')"
          class="shrink-0 w-9 h-9 border border-border rounded-md flex items-center justify-center hover:bg-paper2 transition-colors"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <div class="overflow-y-auto px-4 py-3.5 space-y-3.5 max-h-[70vh]">
        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ t('map.routeEditor.saveDialog.name') }}</span>
          <input
            v-model="name"
            type="text"
            :placeholder="t('map.routeEditor.saveDialog.namePlaceholder')"
            class="w-full h-10 px-3 rounded-md border text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] transition"
            :class="nameError ? 'border-[hsl(var(--danger))] bg-[hsl(var(--danger)/0.05)]' : 'border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] focus:border-[hsl(var(--primary)/0.5)]'"
          />
          <span v-if="nameError" class="text-[11px] text-[hsl(var(--danger))]">{{ nameError }}</span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ t('map.routeEditor.saveDialog.description') }}</span>
          <textarea
            v-model="description"
            rows="3"
            maxlength="250"
            :placeholder="t('map.routeEditor.saveDialog.descriptionPlaceholder')"
            class="w-full px-3 py-2 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition resize-none"
          />
          <span class="self-end num text-[11px] text-ink2">{{ description.length }}/250</span>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-[11px] font-semibold text-ink2">{{ t('map.routeEditor.saveDialog.tags') }}</span>
          <input
            v-model="tagsText"
            type="text"
            :placeholder="t('map.routeEditor.saveDialog.tagsPlaceholder')"
            class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)] focus:border-[hsl(var(--primary)/0.5)] transition"
          />
        </label>

        <div class="flex items-center justify-between gap-3 p-3 rounded-md bg-[hsl(var(--muted)/0.4)]">
          <div class="min-w-0">
            <p class="text-[12.5px] font-semibold">{{ t('map.routeEditor.saveDialog.visibility') }}</p>
            <p class="text-[11px] text-ink2">{{ t('map.routeEditor.saveDialog.visibilityHint') }}</p>
          </div>
          <div class="flex rounded-full border border-border bg-sheet p-0.5 shrink-0">
            <button
              type="button"
              @click="isPublic = false"
              class="h-7 px-2.5 rounded-full text-[11px] font-semibold transition-colors"
              :class="!isPublic ? 'bg-[hsl(var(--primary))] text-white' : 'text-ink2'"
            >{{ t('map.routeEditor.saveDialog.private') }}</button>
            <button
              type="button"
              @click="isPublic = true"
              class="h-7 px-2.5 rounded-full text-[11px] font-semibold transition-colors"
              :class="isPublic ? 'bg-[hsl(var(--primary))] text-white' : 'text-ink2'"
            >{{ t('map.routeEditor.saveDialog.public') }}</button>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2 px-4 py-3 border-t border-[hsl(var(--border)/0.6)] shrink-0">
        <button
          type="button"
          @click="$emit('cancel')"
          class="h-10 px-3 rounded-md bg-[hsl(var(--muted))] text-[12.5px] font-semibold hover:bg-[hsl(var(--muted)/0.8)] transition-colors"
        >
          {{ t('map.routeEditor.saveDialog.cancel') }}
        </button>
        <button
          type="button"
          @click="submit"
          :disabled="saving"
          class="flex-1 h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Loader2 v-if="saving" class="w-4 h-4 animate-spin" />
          {{ t('map.routeEditor.saveDialog.confirm') }}
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { X, Loader2 } from "lucide-vue-next";
import type { RouteSaveMeta } from "../composables/useRouteEditor";

const props = defineProps<{
  isExisting: boolean;
  saving: boolean;
  initial: RouteSaveMeta;
}>();

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "confirm", meta: RouteSaveMeta): void;
}>();

const { t } = useI18n();

const name = ref(props.initial.name);
const description = ref(props.initial.description || "");
const tagsText = ref((props.initial.tags || []).join(", "));
const isPublic = ref(props.initial.is_public);
const nameError = ref("");

const submit = () => {
  const trimmed = name.value.trim();
  if (!trimmed) {
    nameError.value = t("map.routeEditor.saveDialog.nameRequired");
    return;
  }
  nameError.value = "";
  emit("confirm", {
    name: trimmed,
    description: description.value.trim() || null,
    tags: tagsText.value
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    is_public: isPublic.value,
  });
};


// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('cancel') });
</script>
